import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useContext, useCallback } from 'react';
// @mui
import {
  Container,
  Typography,
  Box,
  Stack,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  TextField,
  Alert,
  Tooltip,
  useTheme,
  useMediaQuery,
  Chip,
  CircularProgress,
  Link,
} from '@mui/material';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { AccountContext } from '../account';
import { EntityContext } from '../entity';
import useIP from '../sections/auth/login/useIp';
import { getFingerprint } from '../utils/getFingerprint';
import {
  listCredentials,
  getCredential,
  createCredential,
  updateCredential,
  deleteCredential,
  lookupExternalTemplate,
} from '../services/credentialsApi';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'credential_type', label: 'Tipo', alignRight: false },
  { id: 'template_name', label: 'Template', alignRight: false },
  { id: 'automatic_signature', label: 'Assinatura automática', alignRight: false },
  { id: 'due_date', label: 'Vencimento', alignRight: false },
  { id: 'created_at', label: 'Criado em', alignRight: false },
  { id: 'updated_at', label: 'Atualizado em', alignRight: false },
  { id: 'actions', label: '', alignRight: true },
];

const CREDENTIAL_TYPE_LABELS = {
  DEFAULT: 'Padrão',
  PRIVATE: 'Privada',
  ICP: 'ICP',
};

function formatDateTime(isoString) {
  if (!isoString) return '-';
  const d = isoString.substring(0, 19).replace('T', ' ');
  return d;
}

function formatDate(isoString) {
  if (!isoString) return '-';
  return isoString.substring(0, 10);
}

// Converte arquivo para base64 (apenas a parte após o prefixo data:...;base64,)
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result;
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = reject;
  });
}

// Extrai nome e documento do usuário a partir da sessão (Cognito / getSession)
function getUserDisplayFromSession(session) {
  if (!session) return { userName: 'Usuário', userDocument: '' };
  const givenName = session.given_name ?? session[2]?.Value ?? '';
  const familyName = session.family_name ?? session[3]?.Value ?? '';
  const userName = `${(givenName || '').trim()} ${(familyName || '').trim()}`.trim() || 'Usuário';
  const userDocument = session.user?.username ?? '';
  return { userName, userDocument };
}

// Formata "YYYY-MM-DDTHH:mm" para exibição no termo (DD/MM/YYYY)
function formatDueDateForTerm(dateTimeLocal) {
  if (!dateTimeLocal || dateTimeLocal.length < 10) return '';
  const [y, m, d] = dateTimeLocal.substring(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

// Data de hoje no formato DD/MM/YYYY (para rodapé do termo)
function getTodayFormatted() {
  const d = new Date();
  const day = String(d.getDate()).padStart(2, '0');
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const y = d.getFullYear();
  return `${day}/${m}/${y}`;
}

// Data daqui a 1 ano no formato DD/MM/YYYY (validade padrão quando usuário não informa)
function getOneYearFromTodayFormatted() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  const day = String(d.getDate()).padStart(2, '0');
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const y = d.getFullYear();
  return `${day}/${m}/${y}`;
}

// Retorna string no formato YYYY-MM-DDTHH:mm para min do input (não permitir passado)
function getMinDueDateNow() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
}

// Retorna string no formato YYYY-MM-DDTHH:mm para max do input (máx. 1 ano à frente)
function getMaxDueDateOneYear() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
}

// Monta o texto do termo de outorga com as variáveis preenchidas
function buildTermText({ userName, userDocument, credentialType, templateName, templateKey, validityDisplay }) {
  const tipoLabel = credentialType === 'ICP' ? 'ICP-BRASIL' : 'PRIVADO';
  const templateDisplay = templateKey
    ? `${templateName || templateKey} (${templateKey})`
    : (templateName || 'Nenhum');
  const validadeDisplay = validityDisplay ?? getOneYearFromTodayFormatted();
  const dataDoTermo = getTodayFormatted();
  return `TERMO DE OUTORGA DE PODERES E AUTORIZAÇÃO DE CHANCELA AUTOMÁTICA

IDENTIFICAÇÃO DAS PARTES:
OUTORGANTE (USUÁRIO): ${userName}, inscrito no CPF/CNPJ ${userDocument}, através de suas credenciais de acesso auditadas.
OUTORGADA (PLATAFORMA): Murillo Costa Dalmeida Silva Solucoes em Tecnologia Ltda (SoluSaaS), CNPJ nº 56.182.524/0001-03, sede em São Paulo/SP.

1. DA CONSTITUIÇÃO DO MANDATO ELETRÔNICO
O OUTORGANTE, de livre e espontânea vontade, constitui a OUTORGADA como sua mandatária para a prática específica de assinatura eletrônica automática, conferindo-lhe poderes para utilizar o certificado digital [TIPO: ${tipoLabel}] de sua titularidade, exclusivamente para os fins aqui estabelecidos.

2. DO ESCOPO E LIMITAÇÃO (GATILHOS DE ASSINATURA)
A autorização de assinatura automática é restrita e vinculada aos seguintes critérios técnicos:

Template Vinculado: ${templateDisplay};

Validade: ${validadeDisplay};

Gatilho: Processamento sistêmico via API ou Upload dentro do fluxo do Template citado;

Local de Custódia: Servidores seguros da OUTORGADA, com criptografia de ponta a ponta.

3. DA VALIDADE JURÍDICA (LEI 14.063/2020)
As partes declaram, para todos os fins de direito, que:

As assinaturas realizadas de forma automática possuem a mesma validade jurídica de uma assinatura manuscrita ou eletrônica com interação humana, conforme o Art. 10, § 2º da MP 2.200-2/2001 e a Lei 14.063/2020.

A integridade do documento será garantida pelo cálculo de Hash (SHA-256) e pela aposição de Carimbo de Tempo (Timestamp) em cada ato.

4. DA RESPONSABILIDADE E SEGURANÇA
Configuração: O OUTORGANTE reconhece que a definição das regras de automação (quem assina, o que assina e quando assina) foi feita exclusivamente por ele no painel da plataforma.

Irrevogabilidade Temporária: Cada assinatura realizada automaticamente é considerada um ato jurídico perfeito e acabado, não sendo passível de cancelamento por alegada "falta de clique manual".

Revogação do Termo: O OUTORGANTE poderá revogar esta autorização a qualquer momento, desativando a função "Assinatura Automática", o que cessará novos atos a partir daquele instante.

5. REGISTRO DE EVIDÊNCIAS (AUDIT TRAIL)
A cada assinatura automática, a OUTORGADA gerará um certificado de conformidade contendo o IP de configuração, ID da regra de automação, Hash do arquivo e a evidência deste Aceite Eletrônico.

Data do termo: ${dataDoTermo}`;
}

export default function ChancelaAutomaticaPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const ip = useIP();
  const { getSession } = useContext(AccountContext);
  const { entity, getTemplates } = useContext(EntityContext);
  const [fingerprint, setFingerprint] = useState(null);

  const [token, setToken] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [templatesList, setTemplatesList] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [credentialToDelete, setCredentialToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [credentialToEdit, setCredentialToEdit] = useState(null);
  const [editForm, setEditForm] = useState({ automatic_signature: false });
  const [editLoading, setEditLoading] = useState(false);

  // Create form state
  const [formCredentialType, setFormCredentialType] = useState('PRIVATE');
  const [formTemplateKey, setFormTemplateKey] = useState('');
  const [formAutomaticSignature, setFormAutomaticSignature] = useState(false);
  const [formCertificateFile, setFormCertificateFile] = useState(null);
  const [formCertificatePassword, setFormCertificatePassword] = useState('');
  const [formDueDate, setFormDueDate] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [formTemplateKeyError, setFormTemplateKeyError] = useState(false);

  // Termo de aceite (modal antes de criar credencial)
  const [termAcceptDialogOpen, setTermAcceptDialogOpen] = useState(false);
  const [termAcceptChecked, setTermAcceptChecked] = useState(false);
  const [pendingCreateBody, setPendingCreateBody] = useState(null);
  const [termDisplayText, setTermDisplayText] = useState('');

  // Template externo (fluxo de código de convite)
  const [externalTemplateModalOpen, setExternalTemplateModalOpen] = useState(false);
  const [externalTemplateStep, setExternalTemplateStep] = useState('input'); // 'input' | 'confirm'
  const [externalTemplateKey, setExternalTemplateKey] = useState('');
  const [externalTemplateInfo, setExternalTemplateInfo] = useState(null); // { template_name, entity_name }
  const [externalTemplateLookupLoading, setExternalTemplateLookupLoading] = useState(false);
  const [externalTemplateLookupError, setExternalTemplateLookupError] = useState(null);
  const [selectedExternalTemplate, setSelectedExternalTemplate] = useState(null); // { template_key, template_name, entity_name }

  useEffect(() => {
    getSession()
      .then((data) => setToken(data.idToken.jwtToken))
      .catch(() => setPageError('Erro ao obter sessão.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getSession identity not stable; run once on mount
  }, []);

  useEffect(() => {
    getFingerprint().then(setFingerprint);
  }, []);

  const loadCredentials = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await listCredentials(token, { page: pageNumber, maxResults: 50 });
      setCredentials(data.credentials || []);
      setTotalResults(data.total_results ?? (data.credentials?.length ?? 0));
    } catch (err) {
      setPageError(err.message || 'Erro ao carregar credenciais.');
      setCredentials([]);
    } finally {
      setLoading(false);
    }
  }, [token, pageNumber]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  useEffect(() => {
    if (!entity?.entity_key) {
      setTemplatesList([]);
      return;
    }
    getTemplates()
      .then((data) => {
        if (data && !data.error && data.templates) setTemplatesList(data.templates);
        else setTemplatesList([]);
      })
      .catch(() => setTemplatesList([]));
    // Only re-fetch when entity changes; getTemplates ref is unstable in context
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.entity_key]);

  const handleToggleAddForm = () => {
    if (!showAddForm) setPageError(null);
    setShowAddForm(!showAddForm);
    if (showAddForm) {
      setFormCredentialType('PRIVATE');
      setFormTemplateKey('');
      setFormAutomaticSignature(false);
      setFormCertificateFile(null);
      setFormCertificatePassword('');
      setFormDueDate('');
      setFormTemplateKeyError(false);
      setSelectedExternalTemplate(null);
      setExternalTemplateKey('');
      setExternalTemplateInfo(null);
      setExternalTemplateLookupError(null);
    }
  };

  const handleCreateCredential = async () => {
    if (!token) return;
    if (formCredentialType === 'ICP' && !formCertificateFile) {
      setPageError('Para credencial ICP é obrigatório enviar o certificado (arquivo PFX).');
      return;
    }
    const effectiveTemplateKey = selectedExternalTemplate ? selectedExternalTemplate.template_key : formTemplateKey;
    if (!selectedExternalTemplate && !entity?.entity_key) {
      setFormTemplateKeyError(true);
      return;
    }
    if (!effectiveTemplateKey) {
      setFormTemplateKeyError(true);
      return;
    }
    setFormTemplateKeyError(false);
    setCreateLoading(true);
    try {
      const body = {
        credential_type: formCredentialType,
        automatic_signature: formAutomaticSignature,
        template_key: effectiveTemplateKey,
      };
      if (formDueDate) {
        body.due_date = `${formDueDate}:00Z`;
      }
      if (formCredentialType === 'ICP') {
        body.certificate = await fileToBase64(formCertificateFile);
        if (formCertificatePassword) body.certificate_password = formCertificatePassword;
      }
      const session = await getSession();
      const { userName, userDocument } = getUserDisplayFromSession(session);
      const templateName = selectedExternalTemplate
        ? `${selectedExternalTemplate.template_name} (${selectedExternalTemplate.entity_name})`
        : templatesList.find((t) => t.template_key === effectiveTemplateKey)?.template_name || effectiveTemplateKey;
      const validityDisplay = formDueDate ? formatDueDateForTerm(formDueDate) : getOneYearFromTodayFormatted();
      const text = buildTermText({
        userName,
        userDocument,
        credentialType: formCredentialType,
        templateName,
        templateKey: effectiveTemplateKey,
        validityDisplay,
      });
      setTermDisplayText(text);
      setPendingCreateBody(body);
      setTermAcceptChecked(false);
      setTermAcceptDialogOpen(true);
    } catch (err) {
      setPageError(err.message || 'Erro ao preparar termo.');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCloseTermAcceptDialog = () => {
    setTermAcceptDialogOpen(false);
    setPendingCreateBody(null);
    setTermDisplayText('');
    setTermAcceptChecked(false);
  };

  const handleConfirmTermAccept = async () => {
    if (!token || !pendingCreateBody || !termAcceptChecked) return;
    setCreateLoading(true);
    try {
      const fp = fingerprint != null ? fingerprint : await getFingerprint();
      const evidence = {
        ip: ip || '',
        fingerprint: fp,
        timestamp: new Date().toISOString(),
      };
      const body = {
        ...pendingCreateBody,
        terms_acceptance: {
          accepted_text: termDisplayText || 'Li e aceito o termo acima.',
          evidence,
        },
      };
      await createCredential(token, body);
      handleCloseTermAcceptDialog();
      handleToggleAddForm();
      loadCredentials();
    } catch (err) {
      setPageError(err.message || 'Erro ao criar credencial.');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleOpenDeleteDialog = (cred) => {
    setCredentialToDelete(cred);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCredentialToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!token || !credentialToDelete) return;
    try {
      await deleteCredential(token, credentialToDelete.credential_key);
      loadCredentials();
      handleCloseDeleteDialog();
    } catch (err) {
      setPageError(err.message || 'Erro ao desativar credencial.');
    }
  };

  const handleOpenEditDialog = async (cred) => {
    if (cred.credential_type === 'DEFAULT') return;
    setCredentialToEdit(cred);
    setEditForm({ automatic_signature: cred.automatic_signature ?? false });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setCredentialToEdit(null);
  };

  const handleSaveEdit = async () => {
    if (!token || !credentialToEdit) return;
    setEditLoading(true);
    try {
      await updateCredential(token, credentialToEdit.credential_key, {
        automatic_signature: editForm.automatic_signature,
      });
      loadCredentials();
      handleCloseEditDialog();
    } catch (err) {
      setPageError(err.message || 'Erro ao atualizar credencial.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleCertificateChange = (e) => {
    const file = e.target.files?.[0];
    setFormCertificateFile(file || null);
  };

  const handleOpenExternalTemplateModal = () => {
    setExternalTemplateStep('input');
    setExternalTemplateKey('');
    setExternalTemplateInfo(null);
    setExternalTemplateLookupError(null);
    setExternalTemplateModalOpen(true);
  };

  const handleCloseExternalTemplateModal = () => {
    setExternalTemplateModalOpen(false);
  };

  const handleLookupExternalTemplate = async () => {
    if (!externalTemplateKey.trim()) return;
    setExternalTemplateLookupLoading(true);
    setExternalTemplateLookupError(null);
    try {
      const info = await lookupExternalTemplate(token, externalTemplateKey.trim());
      setExternalTemplateInfo(info);
      setExternalTemplateStep('confirm');
    } catch (err) {
      setExternalTemplateLookupError(err.message);
    } finally {
      setExternalTemplateLookupLoading(false);
    }
  };

  const handleConfirmExternalTemplate = () => {
    setSelectedExternalTemplate({
      template_key: externalTemplateKey.trim(),
      template_name: externalTemplateInfo.template_name,
      entity_name: externalTemplateInfo.entity_name,
    });
    setFormTemplateKey(externalTemplateKey.trim());
    setFormTemplateKeyError(false);
    handleCloseExternalTemplateModal();
  };

  return (
    <>
      <Helmet>
        <title>Chancela Automática</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 2 }, pb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          mb={3}
        >
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Chancela Automática
          </Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={handleToggleAddForm}
            fullWidth={isMobile}
            sx={{ minWidth: { sm: 160 } }}
          >
            Nova credencial
          </Button>
        </Stack>

        {pageError && (
          <Alert severity="error" onClose={() => setPageError(null)} sx={{ mb: 2 }}>
            {pageError}
          </Alert>
        )}

        {showAddForm && (
          <Stack mb={3}>
            <Card sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Nova credencial
              </Typography>
              <Stack spacing={2}>
                {selectedExternalTemplate && (
                  <Chip
                    icon={<Iconify icon="eva:file-text-outline" width={16} />}
                    label={`${selectedExternalTemplate.template_name} — ${selectedExternalTemplate.entity_name}`}
                    onDelete={() => { setSelectedExternalTemplate(null); setFormTemplateKey(''); }}
                    color="primary"
                    variant="outlined"
                    sx={{ width: 'fit-content', maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }}
                  />
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap alignItems={{ sm: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 140, flexShrink: 0 }}>
                    <InputLabel id="create-credential-type-label">Tipo</InputLabel>
                    <Select
                      labelId="create-credential-type-label"
                      label="Tipo"
                      value={formCredentialType}
                      onChange={(e) => setFormCredentialType(e.target.value)}
                    >
                      <MenuItem value="PRIVATE">Privada</MenuItem>
                      <MenuItem value="ICP">ICP</MenuItem>
                    </Select>
                  </FormControl>
                  {!selectedExternalTemplate && (
                    <FormControl size="small" sx={{ minWidth: 180, flexShrink: 0 }} disabled={!entity?.entity_key} error={formTemplateKeyError}>
                      <InputLabel id="create-template-label">Template</InputLabel>
                      <Select
                        labelId="create-template-label"
                        label="Template"
                        value={formTemplateKey}
                        onChange={(e) => { setFormTemplateKey(e.target.value); setFormTemplateKeyError(false); }}
                      >
                        <MenuItem value=""><em>Selecione um template</em></MenuItem>
                        {templatesList.map((t) => (
                          <MenuItem key={t.template_key} value={t.template_key}>{t.template_name || t.template_key}</MenuItem>
                        ))}
                      </Select>
                      {formTemplateKeyError && <FormHelperText>Selecione um template para continuar.</FormHelperText>}
                    </FormControl>
                  )}
                  <TextField
                    label="Data de vencimento (opcional)"
                    type="datetime-local"
                    value={formDueDate}
                    onChange={(e) => setFormDueDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: getMinDueDateNow(), max: getMaxDueDateOneYear() }}
                    size="small"
                    sx={{ minWidth: 220, flexShrink: 0 }}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={formAutomaticSignature} onChange={(e) => setFormAutomaticSignature(e.target.checked)} />}
                    label="Assinatura automática"
                    sx={{ m: 0, flexShrink: 0 }}
                  />
                  {formCredentialType === 'ICP' && (
                    <>
                      <Button variant="outlined" component="label" size="small" sx={{ flexShrink: 0 }}>
                        {formCertificateFile ? formCertificateFile.name : 'Selecionar PFX/P12'}
                        <input type="file" hidden accept=".pfx,.p12,application/x-pkcs12" onChange={handleCertificateChange} />
                      </Button>
                      <TextField
                        size="small"
                        type="password"
                        label="Senha (opcional)"
                        value={formCertificatePassword}
                        onChange={(e) => setFormCertificatePassword(e.target.value)}
                        sx={{ width: 160, flexShrink: 0 }}
                      />
                    </>
                  )}
                </Stack>
              {/* Linha de ação: botões à esquerda, link à direita */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
                sx={{ mt: 2 }}
              >
                <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    variant="contained"
                    onClick={handleCreateCredential}
                    disabled={createLoading || (formCredentialType === 'ICP' && !formCertificateFile)}
                    sx={{ flex: { xs: 1, sm: 'none' } }}
                  >
                    {createLoading ? 'Criando...' : 'Criar'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleToggleAddForm}
                    sx={{ flex: { xs: 1, sm: 'none' } }}
                  >
                    Cancelar
                  </Button>
                </Stack>
                {!selectedExternalTemplate && (
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={handleOpenExternalTemplateModal}
                    sx={{ cursor: 'pointer' }}
                  >
                    Não encontrou o template? Usar código de convite
                  </Link>
                )}
              </Stack>
              </Stack>
              {!entity?.entity_key && !selectedExternalTemplate && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Selecione uma entidade no menu para vincular a credencial a um template, ou use um código de convite para templates de outra organização.
                </Alert>
              )}
            </Card>
          </Stack>
        )}

        <Card>
          {isMobile ? (
            <Stack spacing={2} sx={{ p: 2 }}>
              {loading ? (
                <Typography variant="body2" color="text.secondary" align="center" py={3}>
                  Carregando...
                </Typography>
              ) : credentials.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" py={3}>
                  Nenhuma credencial encontrada.
                </Typography>
              ) : (
                credentials.map((row) => (
                  <Card key={row.credential_key} variant="outlined">
                    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2" color="text.secondary">
                            Tipo
                          </Typography>
                          <Typography variant="body2">
                            {CREDENTIAL_TYPE_LABELS[row.credential_type] ?? row.credential_type}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2" color="text.secondary">
                            Template
                          </Typography>
                          <Typography variant="body2">{row.template_name || '-'}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2" color="text.secondary">
                            Assinatura automática
                          </Typography>
                          <Typography variant="body2">{row.automatic_signature ? 'Sim' : 'Não'}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2" color="text.secondary">
                            Vencimento
                          </Typography>
                          <Typography variant="body2">{formatDate(row.due_date)}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2" color="text.secondary">
                            Criado em
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {formatDateTime(row.created_at)}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={0.5} sx={{ pt: 1 }}>
                          {row.credential_type === 'DEFAULT' ? (
                            <Tooltip title="Credenciais padrão são gerenciadas pela plataforma e não podem ser editadas ou excluídas.">
                              <span>
                                <IconButton size="small" disabled>
                                  <Iconify icon="eva:edit-fill" />
                                </IconButton>
                                <IconButton size="small" disabled>
                                  <Iconify icon="eva:trash-2-outline" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <>
                              <IconButton size="small" color="inherit" onClick={() => handleOpenEditDialog(row)}>
                                <Iconify icon="eva:edit-fill" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(row)}>
                                <Iconify icon="eva:trash-2-outline" />
                              </IconButton>
                            </>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              )}
            </Stack>
          ) : (
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {TABLE_HEAD.map((head) => (
                        <TableCell
                          key={head.id}
                          align={head.alignRight ? 'right' : 'left'}
                        >
                          {head.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        Carregando...
                      </TableCell>
                    </TableRow>
                  ) : credentials.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            Nenhuma credencial encontrada.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      credentials.map((row) => (
                        <TableRow hover key={row.credential_key} tabIndex={-1}>
                        <TableCell>{CREDENTIAL_TYPE_LABELS[row.credential_type] ?? row.credential_type}</TableCell>
                        <TableCell>{row.template_name || '-'}</TableCell>
                        <TableCell>{row.automatic_signature ? 'Sim' : 'Não'}</TableCell>
                        <TableCell>{formatDate(row.due_date)}</TableCell>
                          <TableCell>{formatDateTime(row.created_at)}</TableCell>
                          <TableCell>{row.updated_at ? formatDateTime(row.updated_at) : '-'}</TableCell>
                          <TableCell align="right">
                            {row.credential_type === 'DEFAULT' ? (
                              <Tooltip title="Credenciais padrão são gerenciadas pela plataforma e não podem ser editadas ou excluídas.">
                                <span>
                                  <IconButton size="small" disabled>
                                    <Iconify icon="eva:edit-fill" />
                                  </IconButton>
                                  <IconButton size="small" disabled>
                                    <Iconify icon="eva:trash-2-outline" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <>
                                <IconButton size="small" color="inherit" onClick={() => handleOpenEditDialog(row)}>
                                  <Iconify icon="eva:edit-fill" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(row)}>
                                  <Iconify icon="eva:trash-2-outline" />
                                </IconButton>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} aria-labelledby="delete-dialog-title">
        <DialogTitle id="delete-dialog-title">Confirmar desativação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja desativar esta credencial? A operação é irreversível.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Sim, desativar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={termAcceptDialogOpen}
        onClose={handleCloseTermAcceptDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="term-dialog-title"
      >
        <DialogTitle id="term-dialog-title">
          Termo de outorga de poderes e autorização de chancela automática
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
            <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
              {termDisplayText}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={termAcceptChecked}
                onChange={(e) => setTermAcceptChecked(e.target.checked)}
              />
            }
            label="Li e aceito o termo acima."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTermAcceptDialog} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmTermAccept}
            disabled={!termAcceptChecked || createLoading}
          >
            {createLoading ? 'Cadastrando...' : 'Aceitar e cadastrar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth aria-labelledby="edit-dialog-title">
        <DialogTitle id="edit-dialog-title">Editar credencial</DialogTitle>
        <DialogContent>
          {credentialToEdit && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Credencial
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {credentialToEdit.credential_key}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Template
                </Typography>
                <Typography variant="body2">
                  {credentialToEdit.template_name || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Vencimento
                </Typography>
                <Typography variant="body2">
                  {formatDate(credentialToEdit.due_date)}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editForm.automatic_signature}
                    onChange={(e) => setEditForm((f) => ({ ...f, automatic_signature: e.target.checked }))}
                  />
                }
                label="Assinatura automática"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={editLoading}>
            {editLoading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de código de convite — template externo */}
      <Dialog
        open={externalTemplateModalOpen}
        onClose={handleCloseExternalTemplateModal}
        maxWidth="sm"
        fullWidth
        aria-labelledby="external-template-dialog-title"
      >
        <DialogTitle id="external-template-dialog-title">Código de convite</DialogTitle>
        <DialogContent>
          {externalTemplateStep === 'input' ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Cole abaixo o código enviado pela organização que te convidou para assinar.
              </Typography>
              <TextField
                label="Código do convite"
                value={externalTemplateKey}
                onChange={(e) => {
                  setExternalTemplateKey(e.target.value);
                  setExternalTemplateLookupError(null);
                }}
                size="small"
                fullWidth
                error={!!externalTemplateLookupError}
                helperText={externalTemplateLookupError || 'O código foi enviado por e-mail ou diretamente pelo gestor da organização.'}
                disabled={externalTemplateLookupLoading}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLookupExternalTemplate(); }}
              />
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Template encontrado. Confirme os dados antes de continuar.
              </Typography>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">{externalTemplateInfo?.template_name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {externalTemplateInfo?.entity_name}
                  </Typography>
                </Stack>
              </Card>
              <Typography variant="body2" color="text.secondary">
                Ao continuar, sua credencial será vinculada a este template para assinatura automática.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {externalTemplateStep === 'confirm' ? (
            <>
              <Button onClick={() => setExternalTemplateStep('input')} color="inherit">
                Voltar
              </Button>
              <Button variant="contained" onClick={handleConfirmExternalTemplate}>
                Usar este template
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseExternalTemplateModal} color="inherit">
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleLookupExternalTemplate}
                disabled={!externalTemplateKey.trim() || externalTemplateLookupLoading}
                endIcon={externalTemplateLookupLoading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {externalTemplateLookupLoading ? 'Verificando...' : 'Verificar código'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

    </>
  );
}
